import React, {ChangeEventHandler, FC, HTMLProps, useCallback, useId} from 'react';
import {useAppSelector} from 'core/redux/hooks/useAppSelector';
import {selectCities, selectIsLocationsListLoading} from 'modules/locations/selectors';
import {LogoLoader} from 'components/LegacyLoader';
import style from './style.module.scss';
import classNames from 'classnames';
import {Typography} from 'components/Typography';
import {ICity} from 'modules/locations/models';
import {useAppDispatch} from 'core/redux/hooks/useAppDispatch';
import {applyCity} from 'modules/locations/thunks';
import Icon from 'components/Icon';

export interface IProps extends Omit<HTMLProps<HTMLUListElement>, 'onSelect'> {
    className?: string;
    itemClassName?: string;
    cityNameClassName?: string;
    checkedLocationId?: ICity['id'] | null;
    onSelect: (cityId: ICity['id']) => void;
    disabled?: boolean;
    handleClosePopup?: () => void;
    dataAutotestTarget?: string;
    cityItemTargetPostfix?: string;
}

export const CityList: FC<IProps> = ({
    itemClassName,
    cityNameClassName,
    disabled = false,
    checkedLocationId,
    className,
    onSelect,
    handleClosePopup,
    dataAutotestTarget,
    cityItemTargetPostfix,
    ...rest
}) => {
    const dispatch = useAppDispatch();
    const uid = useId();

    const isCityListLoading = useAppSelector(selectIsLocationsListLoading);
    const cityList = useAppSelector(selectCities);

    const handleCityChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
        (event) => {
            const target = event.target;
            dispatch(applyCity);
            onSelect(Number(target.value));
        },
        [dispatch, onSelect],
    );

    return (
        <ul className={classNames(className, style.list)} {...rest}>
            {isCityListLoading && <LogoLoader />}
            <div className={style.cityPopupHeaderWrapper}>
                <div className={style.cityPopupHeader}>
                    <Typography
                        className={'location__head'}
                        data-autotest-target={'header-location-dropdown'}
                        data-autotest-target-id={'header-location-dropdown-head'}
                        variant={'title-18-m'}
                    >
                        Выберите город
                    </Typography>
                    <button
                        aria-label={'Закрыть выбор города'}
                        className={classNames(style.cityPopClose, 'dropdown__close')}
                        data-autotest-target={`${dataAutotestTarget}-dropdown`}
                        data-autotest-target-id={`${dataAutotestTarget}-dropdown-close`}
                        onClick={handleClosePopup}
                        type={'button'}
                    >
                        <Icon icon={'close'} />
                    </button>
                </div>
            </div>
            {cityList &&
                cityList.map((city) => {
                    return (
                        <li
                            className={classNames(style.item, itemClassName, 'location__item', {
                                ['location__link--active']: checkedLocationId === city.id,
                            })}
                            key={`${uid}--${city.id}`}
                        >
                            <input
                                checked={checkedLocationId === city.id}
                                className={style.input}
                                disabled={disabled}
                                id={String(city.id)}
                                name={uid}
                                onChange={handleCityChange}
                                type={'radio'}
                                value={city.id}
                            />
                            <label
                                className={classNames(style.label, 'header-wineries')}
                                data-autotest-target={`${dataAutotestTarget}-${cityItemTargetPostfix}`}
                                data-autotest-target-id={`${dataAutotestTarget}-${cityItemTargetPostfix}-${city.id}`}
                                data-city={city.id}
                                htmlFor={String(city.id)}
                            >
                                <Typography
                                    className={cityNameClassName}
                                    color={'gray100'}
                                    element={'span'}
                                    variant={'label-16-r'}
                                >
                                    {city.title}
                                </Typography>
                            </label>
                            <Icon className={style.checkMarkIcon} icon={'check-mark'} />
                        </li>
                    );
                })}
            <Typography className={style.text} color={'gray40'} element={'div'} variant={'p-small'}>
                Выбор города влияет на ассортимент товара и возможные способы получения.
            </Typography>
        </ul>
    );
};
